import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User } from '@pdfsign/shared/types';
import { api } from '../services/api';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  initialized: boolean;
  
  // Actions
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  login: (email: string) => Promise<{ devLink?: string; expiresAt?: string; token?: string }>;
  verifyMagicLink: (token: string, purpose: string) => Promise<{ requires_2fa?: boolean; temp_token?: string; email?: string } | void>;
  verify2fa: (tempToken: string, code: string) => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
      initialized: false,

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken });
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      },

      setUser: (user) => set({ user }),

      login: async (email: string) => {
        const response = await api.post('/auth/magic-link', { email, purpose: 'login' });
        return response.data.data;
      },

      verifyMagicLink: async (token: string, _purpose?: string) => {
        const response = await api.post('/auth/verify', { token });
        const data = response.data.data;
        if (data.requires_2fa) {
          return data;
        }
        const { user, accessToken, refreshToken } = data;
        get().setTokens(accessToken, refreshToken);
        set({ user, initialized: true });
        return data;
      },

      verify2fa: async (tempToken: string, code: string) => {
        const response = await api.post('/auth/2fa/verify', { temp_token: tempToken, code });
        const { user, accessToken, refreshToken } = response.data.data;
        get().setTokens(accessToken, refreshToken);
        set({ user, initialized: true });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token');
        
        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          get().setTokens(accessToken, newRefreshToken);
        } catch (error) {
          get().logout();
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // Ignore errors
        }
        set({ user: null, accessToken: null, refreshToken: null, initialized: true });
        delete api.defaults.headers.common['Authorization'];
      },

      initialize: async () => {
        const { accessToken, refreshToken } = get();
        if (!accessToken) {
          set({ initialized: true });
          return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        try {
          const response = await api.get('/auth/me');
          set({ user: response.data.data.user, initialized: true });
        } catch (error) {
          // Try to refresh token
          if (refreshToken) {
            try {
              await get().refreshAccessToken();
              const response = await api.get('/auth/me');
              set({ user: response.data.data.user, initialized: true });
              return;
            } catch {
              // Refresh failed, logout
            }
          }
          set({ user: null, accessToken: null, refreshToken: null, initialized: true });
          delete api.defaults.headers.common['Authorization'];
        }
      },

      updateProfile: async (data) => {
        const response = await api.patch('/users/me', data);
        set({ user: response.data.data });
      },
    }),
    {
      name: 'pdfsign-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
);

// Initialize on app start
if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize();
}

/**
 * Convenience hook: returns the full auth store state (same API as before).
 * Components can use either `useAuth()` or `useAuthStore()`.
 */
export const useAuth = useAuthStore;